import React, { useCallback, useEffect, useState, memo, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  Grid,
  IconButton,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  Tooltip,
  Box,
  useTheme,
} from '@mui/material';
import { useFormik } from 'formik';
import { array, object, string } from 'yup';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import { useVirtualizer } from '@tanstack/react-virtual';
import { v4 as uuidv4 } from 'uuid';
import DialogTransition from './Common/DialogTransition';
import useOrganization from '../hooks/UseOrganization';
import REGEX from '../const/Regex';

const EMPTY_PERMISSIONS = [];

const PermissionItem = memo(
  ({
    name,
    description,
    value,
    index,
    onValueChange,
    onRemove,
    onBlur,
    error,
    touched,
  }) => {
    const theme = useTheme();

    const handleChange = useCallback(
      (e) => {
        onValueChange(index, e.target.value);
      },
      [onValueChange, index]
    );

    const handleBlur = useCallback(() => {
      onBlur(index);
    }, [onBlur, index]);

    const handleRemove = useCallback(() => {
      onRemove(name);
    }, [onRemove, name]);

    return (
      <Grid
        container
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          padding: 2,
          pr: 0,
          mb: 1,
        }}
      >
        <Grid container item xs={11} spacing={2}>
          <Grid item xs={6}>
            <Tooltip
              title={
                <Typography>
                  {description || name.replace(/_/g, ' ')}
                </Typography>
              }
              placement="top"
              arrow
            >
              <TextField
                required
                name={`permissions.${index}.name`}
                label="Permission name"
                placeholder="Enter permission name"
                fullWidth
                variant="outlined"
                value={name.replace(/_/g, ' ')}
                disabled
                size="small"
              />
            </Tooltip>
          </Grid>

          <Grid item xs={6}>
            <TextField
              required
              fullWidth
              variant="outlined"
              label="Value"
              placeholder="Enter value"
              value={value}
              onChange={handleChange}
              onBlur={handleBlur}
              name={`permissions.${index}.value`}
              error={touched && Boolean(error)}
              helperText={touched && error}
              size="small"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              required
              fullWidth
              variant="outlined"
              label="Description"
              placeholder="Enter description"
              multiline
              rows={2}
              value={description || name.replace(/_/g, ' ')}
              disabled
              size="small"
            />
          </Grid>
        </Grid>

        <Grid item xs={1} display="flex" justifyContent="center">
          <IconButton onClick={handleRemove} size="small" title="Delete">
            <DeleteIcon />
          </IconButton>
        </Grid>
      </Grid>
    );
  },
  (prev, next) => {
    return (
      prev.name === next.name &&
      prev.description === next.description &&
      prev.value === next.value &&
      prev.index === next.index &&
      prev.onValueChange === next.onValueChange &&
      prev.onBlur === next.onBlur &&
      prev.onRemove === next.onRemove &&
      prev.error === next.error &&
      prev.touched === next.touched
    );
  }
);

PermissionItem.displayName = 'PermissionItem';

const NewPermissionItem = memo(
  ({ permission, index, onChange, onRemove, onBlur, errors, touched }) => {
    const theme = useTheme();

    const handleNameChange = useCallback(
      (e) => {
        onChange(index, 'name', e.target.value);
      },
      [index, onChange]
    );

    const handleValueChange = useCallback(
      (e) => {
        onChange(index, 'value', e.target.value);
      },
      [index, onChange]
    );

    const handleDescriptionChange = useCallback(
      (e) => {
        onChange(index, 'description', e.target.value);
      },
      [index, onChange]
    );

    const handleNameBlur = useCallback(() => {
      onBlur(index, 'name');
    }, [index, onBlur]);

    const handleValueBlur = useCallback(() => {
      onBlur(index, 'value');
    }, [index, onBlur]);

    const handleDescriptionBlur = useCallback(() => {
      onBlur(index, 'description');
    }, [index, onBlur]);

    const handleRemove = useCallback(() => {
      onRemove(index);
    }, [index, onRemove]);

    return (
      <Grid
        container
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          padding: 2,
          pr: 0,
          mb: 1,
        }}
      >
        <Grid container item xs={11} spacing={2}>
          <Grid item xs={6}>
            <TextField
              required
              fullWidth
              variant="outlined"
              label="Permission name"
              placeholder="Enter permission name"
              value={permission.name}
              onChange={handleNameChange}
              onBlur={handleNameBlur}
              name={`newPermissions.${index}.name`}
              error={touched?.name && Boolean(errors?.name)}
              helperText={touched?.name && errors?.name}
              size="small"
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              required
              fullWidth
              variant="outlined"
              label="Value"
              placeholder="Enter value"
              value={permission.value}
              onChange={handleValueChange}
              onBlur={handleValueBlur}
              name={`newPermissions.${index}.value`}
              error={touched?.value && Boolean(errors?.value)}
              helperText={touched?.value && errors?.value}
              size="small"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              required
              fullWidth
              variant="outlined"
              label="Description"
              placeholder="Enter description"
              multiline
              rows={2}
              value={permission.description}
              onChange={handleDescriptionChange}
              onBlur={handleDescriptionBlur}
              name={`newPermissions.${index}.description`}
              error={touched?.description && Boolean(errors?.description)}
              helperText={touched?.description && errors?.description}
              size="small"
            />
          </Grid>
        </Grid>

        <Grid item xs={1} display="flex" justifyContent="center">
          <IconButton onClick={handleRemove} size="small" title="Delete">
            <DeleteIcon />
          </IconButton>
        </Grid>
      </Grid>
    );
  }
);

NewPermissionItem.displayName = 'NewPermissionItem';

const VirtualizedPermissionsList = memo(
  ({
    permissions,
    errors,
    touched,
    onValueChange,
    onRemove,
    onBlur,
    scrollElement,
    scrollToEnd,
  }) => {
    const parentRef = useRef();
    const virtualizer = useVirtualizer({
      count: permissions.length,
      getScrollElement: () => scrollElement || parentRef.current,
      estimateSize: () => 250,
      overscan: 5,
      getItemKey: (index) => {
        const permission = permissions[index];
        return `unified-${permission.type}-${permission.id || permission.name}-${index}`;
      },
    });

    useEffect(() => {
      if (scrollToEnd && permissions.length > 0) {
        virtualizer.scrollToIndex(permissions.length - 1, {
          align: 'end',
        });
      }
    }, [scrollToEnd, permissions.length, virtualizer]);

    const renderPermissionItem = useCallback(
      (virtualItem) => {
        const permission = permissions[virtualItem.index];
        const { index } = virtualItem;

        if (permission.type === 'existing') {
          return (
            <PermissionItem
              key={permission.name}
              name={permission?.name}
              description={permission?.description}
              value={permission?.value}
              index={index}
              onValueChange={(idx, value) =>
                onValueChange(index, 'value', value)
              }
              onRemove={() => onRemove(index)}
              onBlur={() => onBlur(index)}
              error={errors?.permissions?.[permission.originalIndex]?.value}
              helperText={
                errors?.permissions?.[permission.originalIndex]?.value
              }
              touched={touched?.permissions?.[permission.originalIndex]?.value}
            />
          );
        }

        return (
          <NewPermissionItem
            key={`new-perm-${permission.id}-${index}`}
            permission={permission}
            index={index}
            onChange={(idx, field, value) => onValueChange(index, field, value)}
            onRemove={() => onRemove(index)}
            onBlur={(idx, field) => onBlur(index, field)}
            errors={errors?.newPermissions?.[permission.originalIndex]}
            touched={touched?.newPermissions?.[permission.originalIndex]}
          />
        );
      },
      [permissions, onValueChange, onRemove, onBlur, errors, touched]
    );

    if (permissions.length === 0) {
      return null;
    }

    return (
      <Box ref={parentRef} sx={{ width: '100%' }}>
        <Box
          sx={{
            height: `${virtualizer.getTotalSize()}px`,
            width: '100%',
            position: 'relative',
          }}
        >
          {virtualizer.getVirtualItems().map((virtualItem) => (
            <Box
              key={virtualItem.key}
              data-index={virtualItem.index}
              ref={virtualizer.measureElement}
              sx={{
                position: 'absolute',
                top: `${virtualItem.start}px`,
                left: 0,
                width: '100%',
              }}
            >
              {renderPermissionItem(virtualItem)}
            </Box>
          ))}
        </Box>
      </Box>
    );
  }
);
VirtualizedPermissionsList.displayName = 'VirtualizedPermissionsList';

const CustomPermissionsDialog = ({
  orgId,
  open = false,
  handleClose,
  client,
}) => {
  const { getDefaultPermission, setDefaultPermission } = useOrganization();
  const [permissionId, setPermissionId] = useState(null);
  const theme = useTheme();
  const scrollRef = useRef();
  const [scrollToEnd, setScrollToEnd] = useState(0);

  const validationSchema = object().shape({
    permissions: array().of(
      object().shape({
        name: string().required('Required'),
        value: string()
          .trim()
          .required('Required')
          .matches(
            REGEX.ALPHA_NUMERIC_WITH_SPACES,
            'Only alphanumeric characters allowed'
          ),
        description: string().required('Required'),
      })
    ),
    newPermissions: array().of(
      object().shape({
        name: string()
          .trim()
          .required('Required')
          .matches(
            REGEX.ALPHA_NUMERIC_WITH_SPACES,
            'Only alphanumeric characters allowed'
          )
          .notOneOf(['default'], 'Cannot use "default" as a permission name'),
        value: string()
          .trim()
          .required('Required')
          .matches(
            REGEX.ALPHA_NUMERIC_WITH_SPACES,
            'Only alphanumeric characters allowed'
          ),
        description: string().required('Required'),
      })
    ),
  });

  const formik = useFormik({
    validationSchema,
    initialValues: {
      permissions: [],
      newPermissions: [],
    },
    enableReinitialize: true,
    onSubmit: (values) => {
      const formattedNewPermissions = values.newPermissions
        .filter((perm) => perm.name && perm.value)
        .map((perm) => {
          const { id, ...permWithoutId } = perm;
          return {
            ...permWithoutId,
            name: perm.name.replace(/\s+/g, '_'),
          };
        });
      const allPermissions = [
        ...values.permissions,
        ...formattedNewPermissions,
      ];
      setDefaultPermission(
        {
          product_id: permissionId,
          permissions: allPermissions,
        },
        orgId
      );
      handleClose();
    },
  });

  const { values, errors, handleBlur, handleSubmit, touched, setFieldTouched } =
    formik;

  useEffect(() => {
    getDefaultPermission(client?.id, orgId).then((res) => {
      formik.setFieldValue('permissions', res?.data || []);
      setPermissionId(client?.id);
    });
  }, [client]);

  const handleReset = useCallback(() => {
    getDefaultPermission(client?.id, orgId).then((res) => {
      formik.setFieldValue('permissions', res?.data || []);
      formik.setFieldValue('newPermissions', []);
      formik.setTouched({
        newPermissions: [],
      });
      formik.setErrors({
        newPermissions: [],
      });
      setPermissionId(client?.id);
    });
  }, [client?.id, orgId, getDefaultPermission, formik]);

  const handleAddPermission = useCallback(() => {
    const newPermission = {
      id: uuidv4(),
      name: '',
      value: '',
      description: '',
    };
    formik.setFieldValue('newPermissions', [
      ...values.newPermissions,
      newPermission,
    ]);

    const newIndex = values.newPermissions.length;
    const newTouched = { ...touched };
    const newErrors = { ...errors };

    if (!newTouched.newPermissions) {
      newTouched.newPermissions = [];
    }
    if (!newErrors.newPermissions) {
      newErrors.newPermissions = [];
    }

    newTouched.newPermissions[newIndex] = {
      name: false,
      value: false,
      description: false,
    };

    formik.setTouched(newTouched);

    const totalPermissions =
      values.permissions.length + values.newPermissions.length + 1;

    requestAnimationFrame(() => {
      if (totalPermissions > 5) {
        setScrollToEnd((prev) => prev + 1);
      } else if (scrollRef.current) {
        scrollRef.current.scrollTo({
          top: scrollRef.current.scrollHeight,
          behavior: 'smooth',
        });
      }
    });
  }, [formik, values.newPermissions, values.permissions, touched, errors]);

  const handleNewPermissionChange = useCallback(
    (index, field, value) => {
      formik.setFieldValue(`newPermissions.${index}.${field}`, value);
    },
    [formik.setFieldValue]
  );

  const handleNewPermissionBlur = useCallback(
    (index, field) => {
      setFieldTouched(`newPermissions.${index}.${field}`, true);
    },
    [setFieldTouched]
  );

  const handlePermissionBlur = useCallback(
    (index) => {
      setFieldTouched(`permissions.${index}.value`, true);
    },
    [setFieldTouched]
  );

  const handlePermissionValueChange = useCallback(
    (index, value) => {
      formik.setFieldValue(`permissions.${index}.value`, value);
    },
    [formik.setFieldValue]
  );

  const handlePermissionRemove = useCallback(
    (nameToRemove) => {
      const updatedPermissions = values.permissions.filter(
        (perm) => perm.name !== nameToRemove
      );
      formik.setFieldValue('permissions', updatedPermissions);
    },
    [formik.setFieldValue, values.permissions]
  );

  const handleNewPermissionRemove = useCallback(
    (indexToRemove) => {
      const updatedNewPermissions = values.newPermissions.filter(
        (_, index) => index !== indexToRemove
      );
      formik.setFieldValue('newPermissions', updatedNewPermissions);
    },
    [formik.setFieldValue, values.newPermissions]
  );

  const virtualizedOnValueChange = useCallback(
    (index, field, value) => {
      const permissions = values.permissions || EMPTY_PERMISSIONS;
      const newPermissions = values.newPermissions || EMPTY_PERMISSIONS;

      const allPermissions = [
        ...permissions.map((perm, i) => ({
          ...perm,
          type: 'existing',
          originalIndex: i,
        })),
        ...newPermissions.map((perm, i) => ({
          ...perm,
          type: 'new',
          originalIndex: i,
        })),
      ];
      const permission = allPermissions[index];
      if (permission.type === 'existing') {
        if (field === 'value') {
          handlePermissionValueChange(permission.originalIndex, value);
        }
      } else {
        handleNewPermissionChange(permission.originalIndex, field, value);
      }
    },
    [
      values.permissions,
      values.newPermissions,
      handlePermissionValueChange,
      handleNewPermissionChange,
    ]
  );

  const virtualizedOnRemove = useCallback(
    (indexOrName) => {
      const permissions = values.permissions || EMPTY_PERMISSIONS;
      const newPermissions = values.newPermissions || EMPTY_PERMISSIONS;

      const allPermissions = [
        ...permissions.map((perm, i) => ({
          ...perm,
          type: 'existing',
          originalIndex: i,
        })),
        ...newPermissions.map((perm, i) => ({
          ...perm,
          type: 'new',
          originalIndex: i,
        })),
      ];
      const permission =
        allPermissions[
          typeof indexOrName === 'number'
            ? indexOrName
            : allPermissions.findIndex((p) => p.name === indexOrName)
        ];
      if (permission.type === 'existing') {
        handlePermissionRemove(permission.name);
      } else {
        handleNewPermissionRemove(permission.originalIndex);
      }
    },
    [
      values.permissions,
      values.newPermissions,
      handlePermissionRemove,
      handleNewPermissionRemove,
    ]
  );

  const virtualizedOnBlur = useCallback(
    (index, field) => {
      const permissions = values.permissions || EMPTY_PERMISSIONS;
      const newPermissions = values.newPermissions || EMPTY_PERMISSIONS;

      const allPermissions = [
        ...permissions.map((perm, i) => ({
          ...perm,
          type: 'existing',
          originalIndex: i,
        })),
        ...newPermissions.map((perm, i) => ({
          ...perm,
          type: 'new',
          originalIndex: i,
        })),
      ];
      const permission = allPermissions[index];
      if (permission.type === 'existing') {
        handlePermissionBlur(permission.originalIndex);
      } else if (field) {
        handleNewPermissionBlur(permission.originalIndex, field);
      } else {
        handleBlur();
      }
    },
    [
      values.permissions,
      values.newPermissions,
      handlePermissionBlur,
      handleNewPermissionBlur,
      handleBlur,
    ]
  );

  return (
    <Dialog
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      TransitionComponent={DialogTransition}
      open={open}
    >
      <DialogTitle>Custom Permissions</DialogTitle>
      <IconButton
        onClick={handleClose}
        size="large"
        sx={{ position: 'absolute', right: 16, top: 8 }}
      >
        <CloseIcon style={{ color: theme.palette.other.icon }} />
      </IconButton>
      <DialogContent
        dividers
        ref={scrollRef}
        sx={{
          overflow: 'auto',
          padding: 2,
        }}
      >
        <Box
          flex={1}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'flex-start',
            width: '100%',
          }}
        >
          {(values.permissions?.length > 0 ||
            values.newPermissions?.length > 0) && (
            <>
              {(() => {
                const permissions = values.permissions || EMPTY_PERMISSIONS;
                const newPermissions =
                  values.newPermissions || EMPTY_PERMISSIONS;

                const allPermissions = [
                  ...permissions.map((perm, index) => ({
                    ...perm,
                    type: 'existing',
                    originalIndex: index,
                  })),
                  ...newPermissions.map((perm, index) => ({
                    ...perm,
                    type: 'new',
                    originalIndex: index,
                  })),
                ];
                if (allPermissions.length > 5) {
                  return (
                    <VirtualizedPermissionsList
                      permissions={allPermissions}
                      errors={{
                        permissions: errors.permissions,
                        newPermissions: errors.newPermissions,
                      }}
                      touched={{
                        permissions: touched.permissions,
                        newPermissions: touched.newPermissions,
                      }}
                      scrollElement={scrollRef.current}
                      onValueChange={virtualizedOnValueChange}
                      onRemove={virtualizedOnRemove}
                      onBlur={virtualizedOnBlur}
                      scrollToEnd={scrollToEnd}
                    />
                  );
                }

                return allPermissions.map((permission) => {
                  if (permission.type === 'existing') {
                    return (
                      <PermissionItem
                        key={permission.name}
                        name={permission?.name}
                        description={permission?.description}
                        value={permission?.value}
                        index={permission.originalIndex}
                        onValueChange={handlePermissionValueChange}
                        onRemove={handlePermissionRemove}
                        onBlur={handlePermissionBlur}
                        error={
                          errors.permissions?.[permission.originalIndex]?.value
                        }
                        helperText={
                          errors.permissions?.[permission.originalIndex]?.value
                        }
                        touched={
                          touched.permissions?.[permission.originalIndex]?.value
                        }
                      />
                    );
                  }
                  return (
                    <NewPermissionItem
                      key={`new-perm-${permission.id}`}
                      permission={permission}
                      index={permission.originalIndex}
                      onChange={handleNewPermissionChange}
                      onRemove={handleNewPermissionRemove}
                      onBlur={handleNewPermissionBlur}
                      errors={errors.newPermissions?.[permission.originalIndex]}
                      touched={
                        touched.newPermissions?.[permission.originalIndex]
                      }
                    />
                  );
                });
              })()}
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Button
          variant="outlined"
          color="primary"
          onClick={handleAddPermission}
        >
          + Add Permission
        </Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" onClick={handleReset}>
            Reset
          </Button>
          <Button variant="contained" onClick={handleSubmit}>
            Submit
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default CustomPermissionsDialog;
